import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { SolidButton } from 'components/Button';
import useInputsChanged from 'hooks/useInputsChanged';
import { passengersIcon } from 'assets/svgs/brandIcons';
import FieldTrigger, {
  FieldVariant,
  fieldWrapperClassName,
  panelPositionClassName,
} from './FieldTrigger';

interface IPassengersDropdownProps {
  adults: number;
  children: number;
  childAges: number[];
  onAdultsChange: (n: number) => void;
  onChildrenChange: (n: number) => void;
  onChildAgeChange: (idx: number, age: number) => void;
  // Opens the panel each time it changes: Search asks for a missing child age.
  openRequest?: number;
  variant?: FieldVariant;
}

// WHIT-494: a 44px target around the 28px circle (Apple HIG's smallest for
// a finger), named for screen readers, and disabled at a limit instead of
// doing nothing when pressed.
const stepButtonClassName =
  'group w-11 h-11 -m-2 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed';
const stepCircleClassName =
  'w-7 h-7 rounded-full border border-tt-light-purple flex items-center justify-center text-tt-dark-purple group-enabled:group-hover:bg-tt-verylight-purple';

const PassengersDropdown: React.FC<IPassengersDropdownProps> = ({
  adults,
  children,
  childAges,
  onAdultsChange,
  onChildrenChange,
  onChildAgeChange,
  openRequest,
  variant = 'field',
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Opened from Search (a child's age is missing): adjusted during render,
  // as hooks/useInputsChanged does, not in an effect.
  const openRequested = useInputsChanged([openRequest]);
  if (openRequested && openRequest && !open) setOpen(true);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const label =
    children > 0
      ? `${t('adult', { count: adults })}, ${t('child', { count: children })}`
      : t('adult', { count: adults });

  return (
    <div ref={ref} className={fieldWrapperClassName(variant)}>
      <FieldTrigger
        label={t('Passengers')}
        icon={passengersIcon}
        variant={variant}
        open={open}
        // There is always at least one adult (WHIT-497).
        filled
        onClick={() => setOpen(o => !o)}>
        <span className="text-black">{label}</span>
      </FieldTrigger>

      {open && (
        <div
          className={`absolute z-50 top-full mt-1 ${panelPositionClassName(
            variant,
          )} min-w-[240px] bg-white rounded-xl shadow-lg border border-tt-gray-purple`}>
          <div className="p-3 space-y-3">
            {/* Adults */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('Adults')}</span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => onAdultsChange(Math.max(1, adults - 1))}
                  disabled={adults <= 1}
                  aria-label={t('Remove an adult')}
                  className={stepButtonClassName}
                >
                  <span aria-hidden className={stepCircleClassName}>
                    –
                  </span>
                </button>
                <span className="w-4 text-center text-sm font-semibold">
                  {adults}
                </span>
                <button
                  type="button"
                  onClick={() => onAdultsChange(Math.min(8, adults + 1))}
                  disabled={adults >= 8}
                  aria-label={t('Add an adult')}
                  className={stepButtonClassName}
                >
                  <span aria-hidden className={stepCircleClassName}>
                    +
                  </span>
                </button>
              </div>
            </div>
            {/* Children */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('Children')}</span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => onChildrenChange(Math.max(0, children - 1))}
                  disabled={children <= 0}
                  aria-label={t('Remove a child')}
                  className={stepButtonClassName}
                >
                  <span aria-hidden className={stepCircleClassName}>
                    –
                  </span>
                </button>
                <span className="w-4 text-center text-sm font-semibold">
                  {children}
                </span>
                <button
                  type="button"
                  onClick={() => onChildrenChange(Math.min(4, children + 1))}
                  disabled={children >= 4}
                  aria-label={t('Add a child')}
                  className={stepButtonClassName}
                >
                  <span aria-hidden className={stepCircleClassName}>
                    +
                  </span>
                </button>
              </div>
            </div>
            {/* Child ages */}
            {children > 0 && (
              <div className="space-y-2 pt-1 border-t border-tt-gray-purple">
                <p className="text-xs text-gray-500">{t("Children's ages")}</p>
                {Array.from({ length: children }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm">
                      {t('child_number', { n: i + 1 })}
                    </span>
                    <select
                      value={childAges[i] ?? -1}
                      onChange={e =>
                        onChildAgeChange(i, Number(e.target.value))
                      }
                      className="text-sm border border-tt-light-purple rounded px-2 py-1 focus:outline-hidden focus:ring-1 focus:ring-tt-dark-purple"
                    >
                      <option value={-1}>{t('Select age')}</option>
                      <option value={0}>{t('age_under_one')}</option>
                      {Array.from({ length: 17 }, (_age, j) => j + 1).map(age => (
                        <option key={age} value={age}>
                          {t('age_years', { count: age })}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="p-2 border-t border-tt-gray-purple">
            <SolidButton
              onClick={() => setOpen(false)}
              className="w-full !py-2.5"
            >
              {t('Confirm')}
            </SolidButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default PassengersDropdown;
